<template>
  <div @click.self="onBackdrop">
    <div class="login-header">
      <h2>{{ t('changePassword') }}</h2>
      <!-- The X close button is only present in the voluntary flow.
           In `forced` mode (temp-password gate) the only escape is
           the Logout button below. -->
      <button
        v-if="!forced"
        class="close-btn"
        @click="$emit('logout')"
        :aria-label="t('cancel')"
      >&times;</button>
    </div>

    <div class="form">
      <div v-if="forced" class="info-message">{{ t('tempPasswordNotice') }}</div>

      <input
        v-if="!forced"
        v-model="oldPassword"
        type="password"
        :placeholder="t('currentPassword')"
        autocomplete="current-password"
      />

      <input
        v-model="newPassword"
        type="password"
        :placeholder="t('newPassword')"
        autocomplete="new-password"
        @keyup.enter="submit"
      />
      <input
        v-model="newPasswordRepeat"
        type="password"
        :placeholder="t('repeatNewPassword')"
        autocomplete="new-password"
        @keyup.enter="submit"
      />

      <div v-if="localError || errorMessage" class="error-message">
        {{ localError || errorMessage }}
      </div>
      <div v-if="successMessage" class="success-message">{{ successMessage }}</div>

      <button :disabled="!canSubmit || busy || working" @click="submit">
        {{ t('changePassword') }}
      </button>

      <button class="secondary-btn" @click="$emit('logout')">
        {{ forced ? t('logout') : t('cancel') }}
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from '../i18n/index.js'
import LoginApiService from '../services/LoginApiService.js'

export default defineComponent({
  name: 'ChangePasswordView',

  props: {
    domain: { type: String, default: 'tibtix.com' },
    forced: { type: Boolean, default: false },
    initialOldPassword: { type: String, default: '' },
    busy: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' }
  },

  emits: ['changed', 'logout'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      oldPassword: this.initialOldPassword || '',
      newPassword: '',
      newPasswordRepeat: '',
      localError: '',
      successMessage: '',
      working: false
    }
  },

  computed: {
    canSubmit() {
      if (!this.newPassword || this.newPassword.length < 10) return false
      if (this.newPassword !== this.newPasswordRepeat) return false
      if (!this.forced && !this.oldPassword) return false
      return true
    }
  },

  methods: {
    async submit() {
      this.localError = ''
      this.successMessage = ''
      // Spiegel der Server-Richtlinie (PasswordPolicy, Protect7 #18); der Server prueft verbindlich
      const pw = this.newPassword
      const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(r => r.test(pw)).length
      if (pw.length < 10 || (classes < 3 && pw.length < 16)) {
        this.localError = this.t('passwordTooShort')
        return
      }
      if (this.newPassword !== this.newPasswordRepeat) {
        this.localError = this.t('passwordMismatch')
        return
      }
      this.working = true
      try {
        await LoginApiService.changePassword({
          domain: this.domain,
          oldPassword: this.oldPassword,
          newPassword: this.newPassword
        })
        this.successMessage = this.t('passwordChanged')
        // Pass the new password back so the orchestrator can refresh
        // its stashed credentials before any subsequent re-login
        // (admin path after temp-pw).
        this.$emit('changed', this.newPassword)
      } catch (e) {
        this.localError = (e && e.message) || this.t('passwordChangeFailed')
      } finally {
        this.working = false
      }
    },
    onBackdrop() {
      // Forced mode (temp password) intentionally swallows backdrop
      // clicks — the user has to either set a new password or log
      // out. Otherwise they'd land in a half-authenticated state.
      if (this.forced) return
      this.$emit('logout')
    }
  }
})
</script>

<style scoped>
.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.login-header h2 { margin: 0; font-size: 1.25rem; }
.close-btn {
  border: none; background: none; font-size: 24px; cursor: pointer; color: #6b7280;
}
.close-btn:hover { color: #000; }
.form { display: flex; flex-direction: column; gap: 12px; }
input {
  padding: 10px; border-radius: 6px; border: 1px solid #ddd;
  font: inherit; font-size: 16px;
}
button {
  padding: 12px; background: #2563eb; color: white; border: none;
  border-radius: 8px; font: inherit; font-size: 16px; cursor: pointer;
}
button:disabled { background: #c7c9d1; cursor: not-allowed; }
.secondary-btn { background: #f0f2f5; color: #1a1a2e; }
.secondary-btn:hover { background: #e4e8ee; }
.info-message {
  background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe;
  padding: 10px 12px; border-radius: 6px; font-size: 13px;
}
.error-message {
  background: #fef2f2; color: #dc2626; border: 1px solid #fecaca;
  padding: 10px 12px; border-radius: 6px; font-size: 13px;
}
.success-message {
  background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0;
  padding: 10px 12px; border-radius: 6px; font-size: 13px;
}
</style>
